import { sendRawRequest } from "@api/clients/axios";
import {
  NotificationImportantOutlined as Bell,
  Delete,
  DeleteForever,
  DoneAll,
  Mail,
  Email as MessageSquare,
  Smartphone as PushIcon,
} from "@mui/icons-material";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Divider,
  IconButton,
  Pagination,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { getAdminNotificationPath } from "@repo/types";
import { formatRelative } from "@repo/utils/date";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { NotificationPreferences } from "@/components/notifications/manage-preferences";
import { ManagePreferences } from "@/components/notifications/manage-preferences";
import {
  markNotificationRead,
  NOTIFICATION_PAGE_SIZE,
  type NotificationItem,
  notificationPageQueryOptions,
} from "@/components/notifications/notification-query";
import { useUserContext } from "@/contexts/UserContext/index.context";
import { DEFAULT_PREFERENCES } from "@/utils/constants/notification";
import { useDateLocale } from "@/utils/date-locale";
import { translateNotification } from "@/utils/notificationText";

type NotifierUserState = {
  id?: number;
  user?: { id?: number };
  preferences?: { notification?: NotificationPreferences };
};

// --- Helper Functions ---
function formatPurpose(purpose: string) {
  return purpose
    .split("_")
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(" ");
}

export default function Notifications() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const { userState } = useUserContext();
  const queryClient = useQueryClient();
  const [preferences, setPreferences] = useState<NotificationPreferences>(
    (userState as NotifierUserState | undefined)?.preferences?.notification || DEFAULT_PREFERENCES
  );

  const currentPage = Number.parseInt(searchParams.get("page") || "1", 10);
  const pageSize = NOTIFICATION_PAGE_SIZE;
  const actor = userState as NotifierUserState | undefined;
  const notifierId = actor?.id ?? actor?.user?.id;

  const {
    data: notificationsData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    ...notificationPageQueryOptions(notifierId ?? 0, currentPage),
    enabled: !!notifierId,
  });

  const notifications: NotificationItem[] = notificationsData?.items || [];
  const totalPages = notificationsData?.totalPages || 1;
  const totalNotifications = notificationsData?.total || 0;

  const { data: unreadCount = 0 } = useQuery({
    queryKey: ["notifications", notifierId, "unreadCount"],
    queryFn: async () => {
      if (!notifierId) return 0;
      const response = await sendRawRequest<undefined, number>("get", "notifier/unread/count");
      return response.data;
    },
    enabled: !!notifierId,
    refetchInterval: 30000,
  });

  const invalidateNotifications = () =>
    queryClient.invalidateQueries({ queryKey: ["notifications", notifierId] });

  const deleteNotificationMutation = useMutation({
    mutationFn: async (id: number) => {
      await sendRawRequest<undefined, unknown>("put", `notifier/remove/${id}`);
    },
    onSuccess: invalidateNotifications,
  });

  const deleteAllNotificationsMutation = useMutation({
    mutationFn: async () => {
      await sendRawRequest<undefined, unknown>("put", "notifier/remove/all");
    },
    onSuccess: invalidateNotifications,
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      await sendRawRequest<undefined, unknown>("put", "notifier/read/all");
    },
    onSuccess: invalidateNotifications,
  });

  const deleteNotification = (id: number) => deleteNotificationMutation.mutate(id);
  const deleteAllNotifications = () => deleteAllNotificationsMutation.mutate();
  const markAllAsRead = () => markAllAsReadMutation.mutate();

  const navigateTo = (notification: NotificationItem) => {
    const path = getAdminNotificationPath(notification.metadata);
    if (path) {
      if (!notification.read_at) {
        void markNotificationRead(notification.id)
          .then(invalidateNotifications)
          .catch(() => undefined);
      }
      navigate(path);
    }
  };

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case "EMAIL":
        return <Mail fontSize="small" />;
      case "SMS":
        return <MessageSquare fontSize="small" />;
      case "PUSH":
        return <PushIcon fontSize="small" />;
      default:
        return <Bell fontSize="small" />;
    }
  };

  // --- Render ---
  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          {error instanceof Error ? error.message : "Failed to load notifications"}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      <Paper elevation={0} sx={{ mb: 2, bgcolor: "transparent" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Stack direction="column">
            <Typography variant="h5" fontWeight="bold">
              Notifications
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: "center" }}>
              Showing {Math.min(currentPage * pageSize, totalNotifications)} of {totalNotifications}{" "}
              notifications
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <ManagePreferences
              initialPreferences={preferences}
              onSave={async (prefs) => {
                await sendRawRequest<Record<string, boolean>, unknown>(
                  "put",
                  "notifier/preferences",
                  {
                    pushNotifications: prefs.pushNotifications,
                    emailNotifications: prefs.emailNotifications,
                    smsNotifications: prefs.smsNotifications,
                  }
                );
                setPreferences(prefs);
              }}
            />
            {unreadCount > 0 && (
              <Button
                startIcon={<DoneAll />}
                onClick={markAllAsRead}
                variant="outlined"
                size="small"
                sx={{ textTransform: "none" }}
              >
                Mark all read
              </Button>
            )}
            {totalNotifications > 0 && (
              <Button
                startIcon={<DeleteForever />}
                onClick={deleteAllNotifications}
                size="small"
                variant="outlined"
                color="error"
                sx={{ textTransform: "none" }}
              >
                Remove All
              </Button>
            )}
          </Stack>
        </Box>
      </Paper>

      {/* Notifications List */}
      <Stack spacing={2}>
        <AnimatePresence initial={false} mode="popLayout">
          {totalNotifications === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95 }}
              key="empty-state"
            >
              <Paper sx={{ p: 6, textAlign: "center" }} variant="outlined">
                <Bell sx={{ fontSize: 60, color: "action.disabled", mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  You&apos;re all caught up!
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  No new notifications at this time.
                </Typography>
              </Paper>
            </motion.div>
          ) : (
            notifications.map((notification) => {
              const isUnread = !notification.read_at;
              const text = translateNotification(t, notification);

              return (
                <motion.div
                  key={notification.id}
                  layout
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20, transition: { duration: 0.2 } }}
                >
                  <Card
                    variant="outlined"
                    onClick={() => {
                      navigateTo(notification);
                    }}
                    sx={{
                      position: "relative",
                      cursor: "pointer",
                      borderRadius: 2,
                      borderLeft: "4px solid",
                      borderLeftColor: isUnread ? "primary.main" : "transparent",
                      bgcolor: isUnread ? "action.hover" : "background.paper",
                      boxShadow: isUnread ? theme.shadows[2] : "none",
                      transition: theme.transitions.create(
                        ["background-color", "border-left-color", "box-shadow"],
                        { duration: theme.transitions.duration.standard }
                      ),
                      "&:hover": {
                        bgcolor: isUnread ? "action.selected" : "action.hover",
                      },
                    }}
                  >
                    <Box sx={{ p: 2, display: "flex", gap: 2, alignItems: "flex-start" }}>
                      <Avatar
                        sx={{
                          bgcolor: isUnread ? "primary.main" : "action.disabledBackground",
                          color: isUnread ? "primary.contrastText" : "action.active",
                          width: 40,
                          height: 40,
                          transition: theme.transitions.create(["background-color", "color"], {
                            duration: theme.transitions.duration.standard,
                          }),
                        }}
                      >
                        {getChannelIcon(notification.channel)}
                      </Avatar>

                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: isUnread ? 600 : 400,
                              lineHeight: 1.2,
                              mb: 0.5,
                              transition: theme.transitions.create("font-weight", {
                                duration: theme.transitions.duration.standard,
                              }),
                            }}
                          >
                            {text.title}
                          </Typography>

                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.75}
                            sx={{ ml: 1, flexShrink: 0 }}
                          >
                            {/* Unread indicator: pops out with a small spring when the
                                notification transitions to read, giving a clear "just
                                confirmed" moment rather than an instant disappearance. */}
                            <AnimatePresence initial={false}>
                              {isUnread && (
                                <motion.span
                                  key="unread-dot"
                                  initial={{ scale: 0, opacity: 0 }}
                                  animate={{ scale: 1, opacity: 1 }}
                                  exit={{
                                    scale: 0,
                                    opacity: 0,
                                    transition: { duration: 0.25, ease: "easeOut" },
                                  }}
                                  transition={{ type: "spring", stiffness: 500, damping: 25 }}
                                  style={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: "50%",
                                    backgroundColor: theme.palette.primary.main,
                                    display: "inline-block",
                                  }}
                                />
                              )}
                            </AnimatePresence>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ whiteSpace: "nowrap" }}
                            >
                              {formatRelative(notification.createdAt, { locale: dateLocale })}
                            </Typography>
                          </Stack>
                        </Box>

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {text.content}
                        </Typography>

                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          divider={<Divider orientation="vertical" flexItem />}
                        >
                          {notification.metadata.purpose && (
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: 500, color: "primary.main" }}
                            >
                              {formatPurpose(String(notification.metadata.purpose))}
                            </Typography>
                          )}

                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteNotification(notification.id);
                              }}
                              sx={{ color: "text.secondary", "&:hover": { color: "error.main" } }}
                            >
                              <Delete fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Box>
                    </Box>
                  </Card>
                </motion.div>
              );
            })
          )}
        </AnimatePresence>
      </Stack>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(_, page) => {
              navigate(`?page=${page}`);
            }}
            color="primary"
          />
        </Box>
      )}
    </Container>
  );
}
